// src/lib/analytics.js
//
// Vékony réteg a gtag fölött. MINDEN esemény csak akkor megy ki, ha a látogató
// elfogadta a sütiket – a window.gtag-et kizárólag a CookieBanner hozza létre
// (lásd: src/lib/gtag.js), enélkül a track() csendben nem csinál semmit.

import { useCallback, useEffect, useRef } from "react";
import { GA4_ID } from "./gtag";
import { clarityEvent, claritySetTag, clarityUpgrade } from "./clarity";
import {
  trackMetaLead,
  trackMetaPageView,
  trackMetaPhoneClick,
} from "./meta-pixel";

const MAX_PARAM_LENGTH = 100; // GA4 paraméter-érték limit

function clean(value) {
  if (value === undefined || value === null) return undefined;
  const text = String(value).replace(/\s+/g, " ").trim();
  if (!text) return undefined;
  return text.length > MAX_PARAM_LENGTH ? text.slice(0, MAX_PARAM_LENGTH) : text;
}

export function hasConsent() {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

// Ezeknél a pillanatoknál kérjük a felvétel priorizált megőrzését
const IMPORTANT_EVENTS = [
  "form_submit",
  "form_abandon",
  "form_submit_error",
  "phone_click",
];

// Melyik űrlapból milyen lead születik
export const LEAD_TYPES = {
  arajanlat: "quote_form",
  visszahivas: "callback_popup",
};

// A Metának KIZÁRÓLAG ez a három üzleti esemény megy ki. A részletes mérés
// (görgetés, űrlaplépések, kattintások) a GA4 és a Clarity dolga – a Meta
// felé az csak zaj, és felhígítja az optimalizálási jelet.
//
//   PageView
//   Contact  <- phone_click
//   Lead     <- generate_lead (lead_type: quote_form | callback_popup)
//
// A popup megjelenese es bezarasa szandekosan NEM megy a Metanak, csak a
// valodi Lead jel. Ami nincs ebben a tablaban, az nem jut el a Pixelhez.
const META_EVENTS = {
  page_view: (params) => trackMetaPageView(params),
  phone_click: (params) => trackMetaPhoneClick(params),
  generate_lead: (params) =>
    trackMetaLead(params.lead_type || LEAD_TYPES.arajanlat, params),
};

// Általános event küldés az Analytics propertybe (a Clarity idővonalára és a Meta Pixelbe)
export function track(name, params = {}) {
  if (!hasConsent()) return;

  const payload = { send_to: GA4_ID };

  Object.keys(params).forEach((key) => {
    const raw = params[key];
    const value =
      typeof raw === "number" || typeof raw === "boolean" ? raw : clean(raw);
    if (value !== undefined) payload[key] = value;
  });

  window.gtag("event", name, payload);

  // Clarity: ugyanaz az esemény a session idővonalán, hogy a felvételek
  // között szűrni lehessen rá
  clarityEvent(name);
  if (IMPORTANT_EVENTS.includes(name)) clarityUpgrade(name);

  // Meta Pixel: a send_to a Google sajátja, azt nem küldjük tovább
  const metaParams = Object.assign({}, payload);
  delete metaParams.send_to;
  const sendToMeta = META_EVENTS[name];
  if (sendToMeta) sendToMeta(metaParams);
}

// --- oldalmegtekintés (SPA) --------------------------------------------

export function trackPageView(path) {
  track("page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

// --- kattintások --------------------------------------------------------

const CLICKABLE =
  "a, button, [role='button'], input[type='submit'], input[type='button']";

// A React gyoker es a lapkeret nem mond semmit, azokat atugorjuk
const IGNORED_SECTION_IDS = ["root", "page-wraper"];

function sectionOf(element) {
  let node = element.closest("[id]");
  while (node && IGNORED_SECTION_IDS.includes(node.id)) {
    node = node.parentElement ? node.parentElement.closest("[id]") : null;
  }
  return node ? node.id : undefined;
}

function eventNameFor(element, href, isOutbound) {
  if (href && href.startsWith("tel:")) return "phone_click";
  if (href && href.startsWith("mailto:")) return "email_click";
  if (element.closest(".header-nav, .navbar-nav")) return "menu_click";
  if (isOutbound) return "outbound_click";
  if (element.closest(".site-button, .h-bnr-btn, .btn-half")) return "cta_click";
  return "element_click";
}

function imageAltOf(element) {
  const image = element.querySelector("img[alt]");
  return image ? image.getAttribute("alt") : undefined;
}

export function installClickTracking() {
  if (typeof document === "undefined") return () => {};

  const handler = (event) => {
    const target = event.target;
    if (!target || typeof target.closest !== "function") return;

    const element = target.closest(CLICKABLE);
    if (!element) return;

    const href = element.getAttribute("href") || undefined;

    let isOutbound = false;
    let domain;
    if (href && /^https?:\/\//i.test(href)) {
      try {
        const url = new URL(href, window.location.href);
        domain = url.hostname;
        isOutbound = url.hostname !== window.location.hostname;
      } catch (e) {
        // rossz URL – hagyjuk
      }
    }

    track(eventNameFor(element, href, isOutbound), {
      link_text:
        element.innerText ||
        element.getAttribute("aria-label") ||
        element.getAttribute("title") ||
        imageAltOf(element),
      link_url: href,
      link_domain: domain,
      element_id: element.id,
      element_tag: element.tagName.toLowerCase(),
      section: sectionOf(element),
      page_path: window.location.pathname,
    });
  };

  // capture fázis: akkor is lefut, ha a handler megállítja a buborékolást
  document.addEventListener("click", handler, true);
  return () => document.removeEventListener("click", handler, true);
}

// --- görgetési mélység --------------------------------------------------

const SCROLL_MILESTONES = [25, 50, 75, 90, 100];

export function installScrollTracking() {
  if (typeof window === "undefined") {
    return { reset: () => {}, uninstall: () => {} };
  }

  let reached = [];
  let scheduled = false;

  const measure = () => {
    scheduled = false;

    const doc = document.documentElement;
    const scrollable = doc.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;

    const scrolled = window.scrollY || doc.scrollTop || 0;
    const percent = Math.min(100, Math.round((scrolled / scrollable) * 100));

    SCROLL_MILESTONES.forEach((milestone) => {
      if (percent < milestone || reached.includes(milestone)) return;
      reached.push(milestone);
      track("scroll_depth", {
        percent_scrolled: milestone,
        page_path: window.location.pathname,
      });
    });
  };

  const onScroll = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(measure);
  };

  window.addEventListener("scroll", onScroll, { passive: true });

  return {
    reset: () => {
      reached = [];
    },
    uninstall: () => window.removeEventListener("scroll", onScroll),
  };
}

// --- eltöltött idő ------------------------------------------------------
// Csak az aktív időt számolja: háttérbe tett fülnél megáll.

export function createEngagementTimer() {
  if (typeof document === "undefined") {
    return { elapsed: () => 0, reset: () => {}, uninstall: () => {} };
  }

  let since = document.hidden ? null : Date.now();
  let total = 0;

  const pause = () => {
    if (since === null) return;
    total += Date.now() - since;
    since = null;
  };

  const resume = () => {
    if (since === null) since = Date.now();
  };

  const onVisibilityChange = () => (document.hidden ? pause() : resume());
  document.addEventListener("visibilitychange", onVisibilityChange);

  return {
    elapsed: () => total + (since !== null ? Date.now() - since : 0),
    reset: () => {
      total = 0;
      since = document.hidden ? null : Date.now();
    },
    uninstall: () =>
      document.removeEventListener("visibilitychange", onVisibilityChange),
  };
}

// --- űrlapkövetés -------------------------------------------------------
//
// form_start          – első interakció az űrlappal
// form_field_complete – egy mező először lett kitöltve
// form_error          – validációs hiba
// form_submit         – sikeres beküldés
// form_submit_error   – a beküldés technikai okból nem ment át
// form_abandon        – elkezdte, de elnavigált beküldés nélkül

export function useFormTracking(formName) {
  const state = useRef({
    started: false,
    submitted: false,
    abandoned: false,
    startedAt: 0,
    fields: [],
  });

  const start = useCallback(() => {
    const current = state.current;
    if (current.started) return;
    current.started = true;
    current.startedAt = Date.now();
    track("form_start", {
      form_name: formName,
      page_path: window.location.pathname,
    });
  }, [formName]);

  const field = useCallback(
    (fieldName, filled = true) => {
      start();
      const current = state.current;
      if (!filled || !fieldName || current.fields.includes(fieldName)) return;
      current.fields.push(fieldName);
      track("form_field_complete", {
        form_name: formName,
        field_name: fieldName,
        fields_completed: current.fields.length,
      });
    },
    [formName, start]
  );

  const error = useCallback(
    (fieldName, message) => {
      track("form_error", {
        form_name: formName,
        field_name: fieldName,
        error_message: message,
        fields_completed: state.current.fields.length,
      });
      claritySetTag("form_error_field", fieldName);
    },
    [formName]
  );

  // CSAK valódi, backend által visszaigazolt sikernél hívd!
  const submit = useCallback(
    (params = {}) => {
      const current = state.current;
      // Ez a kapcsoló zárja ki, hogy egy sikeres leadből form_abandon is legyen
      current.submitted = true;

      const common = {
        form_name: formName,
        fields_completed: current.fields.length,
        time_to_submit_sec: current.startedAt
          ? Math.round((Date.now() - current.startedAt) / 1000)
          : undefined,
        ...params,
      };

      // technikai esemény: az űrlap sikeresen elment
      track("form_submit", common);

      // üzleti esemény: új érdeklődőnk van – ez lesz a konverzió
      track("generate_lead", {
        ...common,
        lead_type: LEAD_TYPES[formName] || formName,
      });
    },
    [formName]
  );

  const submitError = useCallback(
    (message) => {
      track("form_submit_error", {
        form_name: formName,
        error_message: message,
      });
    },
    [formName]
  );

  useEffect(() => {
    const reportAbandon = () => {
      const current = state.current;
      if (!current.started || current.submitted || current.abandoned) return;
      current.abandoned = true;
      track("form_abandon", {
        form_name: formName,
        fields_completed: current.fields.length,
        last_field: current.fields[current.fields.length - 1],
        time_spent_sec: current.startedAt
          ? Math.round((Date.now() - current.startedAt) / 1000)
          : undefined,
      });
      claritySetTag("form_abandon_at", current.fields.length);
    };

    window.addEventListener("pagehide", reportAbandon);
    return () => {
      window.removeEventListener("pagehide", reportAbandon);
      reportAbandon();
    };
  }, [formName]);

  return { start, field, error, submit, submitError };
}
